import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
function VaccinationManagement() {
    const [tab, setTab] = useState("category");
    /*
    ============================================================
       VACCINE MASTER SUB TAB
    ============================================================
    */
    const [vaccineSection, setVaccineSection] = useState("master");
    /*
    ============================================================
       MASTER DATA
    ============================================================
    */
    const [categories, setCategories] = useState([]);
    const [vaccines, setVaccines] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [scheduleDoses, setScheduleDoses] = useState([]);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [batchLoading, setBatchLoading] = useState(false);
    const [savingBatch, setSavingBatch] = useState(false);
    /*
    ============================================================
       CATEGORY FORM
    ============================================================
    */
    const [categoryForm, setCategoryForm] = useState({
        category_code: "",
        category_name: "",
        description: "",
        status: "ACTIVE",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    });
    /*
    ============================================================
       VACCINE FORM
    ============================================================
    */
    const [vaccineForm, setVaccineForm] = useState({
        vaccine_code: "",
        vaccine_name: "",
        category: "",
        manufacturer: "",
        description: "",
        route: "",
        default_dose: "",
        status: "ACTIVE",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    });
    /*
    ============================================================
       BATCH FORM
    ============================================================
    */
    const [batchForm, setBatchForm] = useState({
        vaccine: "",
        batch_number: "",
        manufacturer: "",
        manufacturing_date: "",
        expiry_date: "",
        quantity: "",
        available_quantity: "",
        storage_location: "",
        remarks: ""
    });
    /*
    ============================================================
       SCHEDULE FORM
    ============================================================
    */
    const [scheduleForm, setScheduleForm] = useState({
        schedule_code: "",
        schedule_name: "",
        description: "",
        gender: "",
        status: "ACTIVE",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    });
    /*
    ============================================================
       DOSE FORM
    ============================================================
    */
    const [doseForm, setDoseForm] = useState({
        schedule: "",
        vaccine: "",
        dose_number: 1,
        dose_name: "",
        age_value: "",
        age_unit: "",
        interval_value: "",
        interval_unit: "",
        minimum_interval_value: "",
        minimum_interval_unit: "",
        booster: false,
        status: "ACTIVE",
        remarks: "",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    });
    /*
    ============================================================
       LOAD ALL DATA
    ============================================================
    */
    useEffect(() => {
        loadAllData();
    }, []);
    const loadAllData = async () => {
        setLoading(true);
        try {
            const [categoryResponse, vaccineResponse, scheduleResponse, doseResponse] = await Promise.all([axios.get(`${baseUrl}vaccination/categories/`), axios.get(`${baseUrl}vaccination/vaccines/`), axios.get(`${baseUrl}vaccination/schedules/`), axios.get(`${baseUrl}vaccination/schedule-doses/`)]);
            setCategories(categoryResponse.data?.results || categoryResponse.data || []);
            setVaccines(vaccineResponse.data?.results || vaccineResponse.data || []);
            setSchedules(scheduleResponse.data?.results || scheduleResponse.data || []);
            setScheduleDoses(doseResponse.data?.results || doseResponse.data || []);
            /*
            ========================================================
               ALSO LOAD BATCH STOCK
            ========================================================
            */
            await loadBatches();
        } catch (error) {
            console.error("Vaccination master loading error:", error);
        } finally {
            setLoading(false);
        }
    };
    /*
    ============================================================
       LOAD BATCHES
    ============================================================
    */
    const loadBatches = async () => {
        setBatchLoading(true);
        try {
            const response = await axios.get(`${baseUrl}vaccination/batches/`);
            setBatches(response.data?.results || response.data || []);
        } catch (error) {
            console.error("Vaccine batch loading error:", error);
        } finally {
            setBatchLoading(false);
        }
    };
    /*
    ============================================================
       CATEGORY FORM CHANGE
    ============================================================
    */
    const handleCategoryChange = (e) => {
        const {
            name, value
        } = e.target;
        setCategoryForm(prev => ({
            ...prev, [name]: value
        }));
    };
    /*
    ============================================================
       VACCINE FORM CHANGE
    ============================================================
    */
    const handleVaccineChange = (e) => {
        const {
            name, value
        } = e.target;
        setVaccineForm(prev => ({
            ...prev, [name]: value
        }));
    };
    /*
    ============================================================
       BATCH FORM CHANGE
    ============================================================
    */
    const handleBatchChange = (e) => {
        const {
            name, value
        } = e.target;
        setBatchForm(prev => ({
            ...prev, [name]: value
        }));
    };
    /*
    ============================================================
       SCHEDULE FORM CHANGE
    ============================================================
    */
    const handleScheduleChange = (e) => {
        const {
            name, value
        } = e.target;
        setScheduleForm(prev => ({
            ...prev, [name]: value
        }));
    };
    /*
    ============================================================
       DOSE FORM CHANGE
    ============================================================
    */
    const handleDoseChange = (e) => {
        const {
            name, value, type, checked
        } = e.target;
        setDoseForm(prev => ({
            ...prev, [name]: type === "checkbox" ? checked : value
        }));
    };
    /*
    ============================================================
       ADD CATEGORY
    ============================================================
    */
    const saveCategory = () => {
        if (!categoryForm.category_code || !categoryForm.category_name) {
            Swal.fire("Required", "Category Code and Category Name are required.", "warning");
            return;
        }
        axios.post(`${baseUrl}vaccination/categories/`, categoryForm)
            .then(() => {
                Swal.fire("Success", "Vaccination category added successfully.", "success");
                setCategoryForm({
                    category_code: "",
                    category_name: "",
                    description: "",
                    status: "ACTIVE",
                    addedby: userName,
                    ip_add: ipAddress,
                    location: userLocation
                });
                loadAllData();
            })
            .catch(error => {
                console.error(error);
                Swal.fire("Error", "Unable to save vaccination category.", "error");
            });
    };
    /*
    ============================================================
       ADD VACCINE
    ============================================================
    */
    const saveVaccine = () => {
        if (!vaccineForm.vaccine_code || !vaccineForm.vaccine_name) {
            Swal.fire("Required", "Vaccine Code and Vaccine Name are required.", "warning");
            return;
        }
        const data = {
            ...vaccineForm, category: vaccineForm.category || null
        };
        axios.post(`${baseUrl}vaccination/vaccines/`, data)
            .then(() => {
                Swal.fire("Success", "Vaccine added successfully.", "success");
                setVaccineForm({
                    vaccine_code: "",
                    vaccine_name: "",
                    category: "",
                    manufacturer: "",
                    description: "",
                    route: "",
                    default_dose: "",
                    status: "ACTIVE",
                    addedby: userName,
                    ip_add: ipAddress,
                    location: userLocation
                });
                loadAllData();
            })
            .catch(error => {
                console.error(error);
                Swal.fire("Error", "Unable to save vaccine.", "error");
            });
    };
    /*
    ============================================================
       ADD BATCH
    ============================================================
    */
    const saveBatch = async () => {
        if (!batchForm.vaccine) {
            Swal.fire(
                "Required",
                "Please select a vaccine.",
                "warning"
            );
            return;
        }
        if (!batchForm.batch_number) {
            Swal.fire(
                "Required",
                "Batch number is required.",
                "warning"
            );
            return;
        }
        if (!batchForm.expiry_date) {
            Swal.fire(
                "Required",
                "Expiry date is required.",
                "warning"
            );
            return;
        }
        if (!batchForm.quantity || Number(batchForm.quantity) <= 0) {
            Swal.fire(
                "Required",
                "Total quantity must be greater than 0.",
                "warning"
            );
            return;
        }
        setSavingBatch(true);
        try {
            /* =====================================================
               TODAY
            ===================================================== */
            const today =
                new Date().toISOString().split("T")[0];
            /* =====================================================
               QUANTITY
            ===================================================== */
            const quantityReceived =
                Number(batchForm.quantity || 0);
            const quantityAvailable =
                batchForm.available_quantity === ""
                    ? quantityReceived
                    : Number(batchForm.available_quantity);
            /* =====================================================
               PAYLOAD
            ===================================================== */
            const payload = {
                vaccine: Number(batchForm.vaccine),
                batch_number:
                batchForm.batch_number,
                manufacturer:
                batchForm.manufacturer,
                manufacturing_date:
                    batchForm.manufacturing_date || null,
                expiry_date:
                batchForm.expiry_date,
                /* ================================
                   STOCK
                ================================= */

                quantity_received:
                quantityReceived,
                quantity_available:
                quantityAvailable,
                quantity_used:
                    0,
                reorder_level:
                    0,
                /* ================================
                   RECEIVING
                ================================= */

                received_date:
                today,
                /* ================================
                   STORAGE
                ================================= */

                storage_location:
                batchForm.storage_location,
                /* ================================
                   STATUS
                ================================= */

                status:
                    "ACTIVE",
                remarks:
                batchForm.remarks,
                /* ================================
                   AUDIT
                ================================= */

                addedby:
                    userName || null,
                ip_add:
                    ipAddress || null,
                location:
                    userLocation || 1
            };
            console.log(
                "VACCINE BATCH PAYLOAD",
                payload
            );
            /* =====================================================
               SAVE
            ===================================================== */
            await axios.post(
                `${baseUrl}vaccination/batches/`,
                payload
            );
            await Swal.fire(
                "Success",
                "Vaccine batch added successfully.",
                "success"
            );
            /* =====================================================
               RESET
            ===================================================== */
            setBatchForm({
                vaccine: "",
                batch_number: "",
                manufacturer: "",
                manufacturing_date: "",
                expiry_date: "",
                quantity: "",
                available_quantity: "",
                storage_location: "",
                remarks: ""
            });
            /* =====================================================
               RELOAD
            ===================================================== */
            await loadBatches();
        } catch (error) {
            console.error(
                "Batch save error:",
                error
            );
            const backendError =
                error.response?.data;
            let message =
                "Unable to save vaccine batch.";
            if (
                backendError &&
                typeof backendError === "object"
            ) {
                message =
                    Object.entries(backendError)
                        .map(
                            ([field, value]) =>
                                `${field}: ${
                                    Array.isArray(value)
                                        ? value.join(", ")
                                        : String(value)
                                }`
                        )
                        .join("\n");
            }
            Swal.fire(
                "Error",
                message,
                "error"
            );
        } finally {
            setSavingBatch(false);
        }
    };
    /*
    ============================================================
       ADD SCHEDULE
    ============================================================
    */
    const saveSchedule = () => {
        if (!scheduleForm.schedule_code || !scheduleForm.schedule_name) {
            Swal.fire("Required", "Schedule Code and Schedule Name are required.", "warning");
            return;
        }
        axios.post(`${baseUrl}vaccination/schedules/`, scheduleForm)
            .then(() => {
                Swal.fire("Success", "Vaccination schedule added successfully.", "success");
                setScheduleForm({
                    schedule_code: "",
                    schedule_name: "",
                    description: "",
                    gender: "",
                    status: "ACTIVE",
                    addedby: userName,
                    ip_add: ipAddress,
                    location: userLocation
                });
                loadAllData();
            })
            .catch(error => {
                console.error(error);
                Swal.fire("Error", "Unable to save vaccination schedule.", "error");
            });
    };
    /*
    ============================================================
       ADD DOSE
    ============================================================
    */
    const saveDose = () => {
        if (!doseForm.schedule || !doseForm.vaccine) {
            Swal.fire("Required", "Schedule and Vaccine are required.", "warning");
            return;
        }
        const data = {
            ...doseForm,
            dose_number: Number(doseForm.dose_number),
            age_value: doseForm.age_value ? Number(doseForm.age_value) : null,
            interval_value: doseForm.interval_value ? Number(doseForm.interval_value) : null,
            minimum_interval_value: doseForm.minimum_interval_value ? Number(doseForm.minimum_interval_value) : null
        };
        axios.post(`${baseUrl}vaccination/schedule-doses/`, data)
            .then(() => {
                Swal.fire("Success", "Vaccination schedule dose added successfully.", "success");
                setDoseForm({
                    schedule: "",
                    vaccine: "",
                    dose_number: 1,
                    dose_name: "",
                    age_value: "",
                    age_unit: "",
                    interval_value: "",
                    interval_unit: "",
                    minimum_interval_value: "",
                    minimum_interval_unit: "",
                    booster: false,
                    status: "ACTIVE",
                    remarks: "",
                    addedby: userName,
                    ip_add: ipAddress,
                    location: userLocation
                });
                loadAllData();
            })
            .catch(error => {
                console.error(error);
                Swal.fire("Error", "Unable to save vaccination schedule dose.", "error");
            });
    };
    /*
    ============================================================
       BATCH HELPERS
    ============================================================
    */
    const getVaccineNameFromBatch = (batch) => {
        if (batch?.vaccine_name) {
            return batch.vaccine_name;
        }
        if (batch?.vaccine && typeof batch.vaccine === "object") {
            return (batch.vaccine.vaccine_name || batch.vaccine.name || batch.vaccine.my_title || "-");
        }
        if (batch?.vaccine) {
            const vaccine = vaccines.find(item => String(item.id) === String(batch.vaccine));
            return (vaccine?.vaccine_name || vaccine?.name || vaccine?.my_title || batch.vaccine);
        }
        return "-";
    };
    const isExpired = (date) => {
        if (!date) {
            return false;
        }
        const expiry = new Date(date);
        expiry.setHours(23, 59, 59, 999);
        return expiry < new Date();
    };
    const getStockStatus = (batch) => {
        if (!batch) {
            return {
                label: "Unknown",
                color: "danger"
            };
        }
        /* =====================================================
           AVAILABLE QUANTITY
           ===================================================== */
        const received = Number(
            batch.quantity_received ?? 0
        );
        const used = Number(
            batch.quantity_used ?? 0
        );
        const available = Number(
            batch.quantity_available ?? (received - used)
        );
        /* =====================================================
           DEBUG
           ===================================================== */
        console.log("STOCK STATUS CHECK:", {
            batch: batch.batch_number,
            received,
            used,
            available,
            reorder: batch.reorder_level,
            expiry: batch.expiry_date
        });
        /* =====================================================
           EXPIRY
           ===================================================== */
        if (batch.expiry_date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const expiry = new Date(
                `${batch.expiry_date}T00:00:00`
            );
            expiry.setHours(0, 0, 0, 0);
            if (expiry < today) {
                return {
                    label: "Expired",
                    color: "danger"
                };
            }
        }
        /* =====================================================
           OUT OF STOCK
           ===================================================== */
        if (available <= 0) {
            return {
                label: "Out of Stock",
                color: "danger"
            };
        }
        /* =====================================================
           LOW STOCK
           ===================================================== */
        const reorderLevel = Number(
            batch.reorder_level ?? 0
        );
        if (
            reorderLevel > 0 &&
            available <= reorderLevel
        ) {
            return {
                label: "Low Stock",
                color: "warning"
            };
        }
        /* =====================================================
           AVAILABLE
           ===================================================== */
        return {
            label: "Available",
            color: "success"
        };
    };
    /*
    ============================================================
       VACCINE COLUMNS
    ============================================================
    */
    const vaccineColumns = useMemo(() => [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "vaccine_code", headerName: "Vaccine Code", width: 150
    }, {
        field: "vaccine_name", headerName: "Vaccine Name", flex: 1, minWidth: 200
    }, {
        field: "category", headerName: "Category", width: 180, renderCell: (params) => {
            const category = params.row?.category;
            if (!category) {
                return "";
            }
            if (typeof category === "object") {
                return (category.category_name || "");
            }
            const foundCategory = categories.find(item => String(item.id) === String(category));
            return foundCategory ? foundCategory.category_name : category;
        }
    }, {
        field: "manufacturer", headerName: "Manufacturer", width: 180
    }, {
        field: "route", headerName: "Route", width: 130
    }, {
        field: "default_dose", headerName: "Default Dose", width: 130
    }, {
        field: "status", headerName: "Status", width: 110
    }], [categories]);
    /*
    ============================================================
       CATEGORY COLUMNS
    ============================================================
    */
    const categoryColumns = useMemo(() => [{
        field: "id", headerName: "ID", width: 80
    }, {
        field: "category_code", headerName: "Category Code", width: 160
    }, {
        field: "category_name", headerName: "Category Name", flex: 1, minWidth: 220
    }, {
        field: "description", headerName: "Description", flex: 1, minWidth: 250
    }, {
        field: "status", headerName: "Status", width: 120
    }, {
        field: "addedby", headerName: "Added By", width: 150
    }], []);
    /*
    ============================================================
       SCHEDULE COLUMNS
    ============================================================
    */
    const scheduleColumns = useMemo(() => [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "schedule_code", headerName: "Schedule Code", width: 160
    }, {
        field: "schedule_name", headerName: "Schedule Name", flex: 1, minWidth: 220
    }, {
        field: "description", headerName: "Description", flex: 1, minWidth: 250
    }, {
        field: "gender", headerName: "Gender", width: 120
    }, {
        field: "status", headerName: "Status", width: 110
    }], []);
    /*
    ============================================================
       DOSE COLUMNS
    ============================================================
    */
    const doseColumns = useMemo(() => [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "schedule", headerName: "Schedule", flex: 1, minWidth: 220, renderCell: (params) => {
            const schedule = params.row?.schedule;
            if (!schedule) {
                return "";
            }
            if (typeof schedule === "object") {
                return (schedule.schedule_name || "");
            }
            const foundSchedule = schedules.find(item => String(item.id) === String(schedule));
            return foundSchedule ? foundSchedule.schedule_name : schedule;
        }
    }, {
        field: "vaccine", headerName: "Vaccine", flex: 1, minWidth: 200, renderCell: (params) => {
            const vaccine = params.row?.vaccine;
            if (!vaccine) {
                return "";
            }
            if (typeof vaccine === "object") {
                return (vaccine.vaccine_name || vaccine.name || "");
            }
            const foundVaccine = vaccines.find(item => String(item.id) === String(vaccine));
            return foundVaccine ? foundVaccine.vaccine_name : vaccine;
        }
    }, {
        field: "dose_number", headerName: "Dose", width: 90
    }, {
        field: "dose_name", headerName: "Dose Name", width: 150
    }, {
        field: "age_value", headerName: "Age", width: 90
    }, {
        field: "age_unit", headerName: "Age Unit", width: 100
    }, {
        field: "interval_value", headerName: "Interval", width: 100
    }, {
        field: "interval_unit", headerName: "Interval Unit", width: 120
    }, {
        field: "booster", headerName: "Booster", width: 100, type: "boolean"
    }, {
        field: "status", headerName: "Status", width: 110
    }], [schedules, vaccines]);
    /*
    ============================================================
       PAGE
    ============================================================
    */
    return (<div className="container-fluid py-3">

        {/* ====================================================
                HEADER
            ==================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-body">

                <div className="d-flex justify-content-between align-items-center">

                    <div>

                        <h4 className="mb-1">
                            Vaccination Management
                        </h4>

                        <small className="text-muted">
                            Vaccination categories, vaccines,
                            batches, stock, schedules and
                            dose configuration
                        </small>

                    </div>


                    <button
                        className="btn btn-outline-success"
                        onClick={loadAllData}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>

                </div>

            </div>

        </div>


        {/* ====================================================
                MAIN TABS
            ==================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-body p-2">

                <div className="d-flex flex-wrap gap-2">

                    <button
                        className={`btn ${tab === "category" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("category")}
                    >
                        Category Master
                    </button>


                    <button
                        className={`btn ${tab === "vaccine" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("vaccine")}
                    >
                        Vaccine Master
                    </button>


                    <button
                        className={`btn ${tab === "schedule" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("schedule")}
                    >
                        Schedule Master
                    </button>


                    <button
                        className={`btn ${tab === "dose" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("dose")}
                    >
                        Schedule Dose
                    </button>

                </div>

            </div>

        </div>


        {/* ====================================================
                CATEGORY MASTER
            ==================================================== */}

        {tab === "category" && (<>

            <div className="card shadow-sm mb-3">

                <div className="card-header">

                    <strong>
                        Vaccination Category
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row g-3">

                        <div className="col-md-3">

                            <label className="form-label">
                                Category Code
                            </label>

                            <input
                                type="text"
                                name="category_code"
                                className="form-control"
                                value={categoryForm.category_code}
                                onChange={handleCategoryChange}
                                placeholder="Example: CHILD"
                            />

                        </div>


                        <div className="col-md-4">

                            <label className="form-label">
                                Category Name
                            </label>

                            <input
                                type="text"
                                name="category_name"
                                className="form-control"
                                value={categoryForm.category_name}
                                onChange={handleCategoryChange}
                                placeholder="Category Name"
                            />

                        </div>


                        <div className="col-md-5">

                            <label className="form-label">
                                Description
                            </label>

                            <input
                                type="text"
                                name="description"
                                className="form-control"
                                value={categoryForm.description}
                                onChange={handleCategoryChange}
                                placeholder="Description"
                            />

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Status
                            </label>

                            <select
                                name="status"
                                className="form-select"
                                value={categoryForm.status}
                                onChange={handleCategoryChange}
                            >

                                <option value="ACTIVE">
                                    ACTIVE
                                </option>

                                <option value="INACTIVE">
                                    INACTIVE
                                </option>

                            </select>

                        </div>


                        <div className="col-md-9 d-flex align-items-end">

                            <button
                                className="btn btn-success"
                                onClick={saveCategory}
                            >
                                Add Category
                            </button>

                        </div>

                    </div>

                </div>

            </div>


            <div className="card shadow-sm">

                <div className="card-header">

                    <strong>
                        Category List
                    </strong>

                </div>


                <div
                    className="card-body"
                    style={{height: 420}}
                >

                    <DataGrid
                        rows={categories}
                        columns={categoryColumns}
                        loading={loading}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10, page: 0
                                }
                            }
                        }}
                        slots={{
                            toolbar: GridToolbar
                        }}
                    />

                </div>

            </div>

        </>)}


        {/* ====================================================
                VACCINE MASTER + BATCH & STOCK
            ==================================================== */}

        {tab === "vaccine" && (<>

            {/* ====================================================
                        VACCINE SUB TABS
                    ==================================================== */}

            <div className="card shadow-sm mb-3">

                <div className="card-body p-2">

                    <div className="d-flex flex-wrap gap-2">

                        <button
                            className={`btn ${vaccineSection === "master" ? "btn-success" : "btn-outline-success"}`}
                            onClick={() => setVaccineSection("master")}
                        >
                            Vaccine Details
                        </button>


                        <button
                            className={`btn ${vaccineSection === "batch" ? "btn-success" : "btn-outline-success"}`}
                            onClick={() => setVaccineSection("batch")}
                        >
                            Batch & Stock
                            {batches.length > 0 && (<span className="ms-2 badge bg-secondary">
                                            {batches.length}
                                        </span>)}
                        </button>

                    </div>

                </div>

            </div>


            {/* ====================================================
                        VACCINE DETAILS
                    ==================================================== */}

            {vaccineSection === "master" && (<>

                <div className="card shadow-sm mb-3">

                    <div className="card-header">

                        <strong>
                            Vaccine Master
                        </strong>

                    </div>


                    <div className="card-body">

                        <div className="row g-3">

                            <div className="col-md-3">

                                <label className="form-label">
                                    Vaccine Code
                                </label>

                                <input
                                    type="text"
                                    name="vaccine_code"
                                    className="form-control"
                                    value={vaccineForm.vaccine_code}
                                    onChange={handleVaccineChange}
                                    placeholder="Vaccine Code"
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Vaccine Name
                                </label>

                                <input
                                    type="text"
                                    name="vaccine_name"
                                    className="form-control"
                                    value={vaccineForm.vaccine_name}
                                    onChange={handleVaccineChange}
                                    placeholder="Vaccine Name"
                                />

                            </div>


                            <div className="col-md-5">

                                <label className="form-label">
                                    Category
                                </label>

                                <select
                                    name="category"
                                    className="form-select"
                                    value={vaccineForm.category}
                                    onChange={handleVaccineChange}
                                >

                                    <option value="">
                                        Select Category
                                    </option>

                                    {categories.map(category => (<option
                                        key={category.id}
                                        value={category.id}
                                    >
                                        {category.category_name}
                                    </option>))}

                                </select>

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Manufacturer
                                </label>

                                <input
                                    type="text"
                                    name="manufacturer"
                                    className="form-control"
                                    value={vaccineForm.manufacturer}
                                    onChange={handleVaccineChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Route
                                </label>

                                <input
                                    type="text"
                                    name="route"
                                    className="form-control"
                                    value={vaccineForm.route}
                                    onChange={handleVaccineChange}
                                    placeholder="IM / SC / Oral"
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Default Dose
                                </label>

                                <input
                                    type="text"
                                    name="default_dose"
                                    className="form-control"
                                    value={vaccineForm.default_dose}
                                    onChange={handleVaccineChange}
                                    placeholder="Example: 0.5 ml"
                                />

                            </div>


                            <div className="col-md-8">

                                <label className="form-label">
                                    Description
                                </label>

                                <input
                                    type="text"
                                    name="description"
                                    className="form-control"
                                    value={vaccineForm.description}
                                    onChange={handleVaccineChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Status
                                </label>

                                <select
                                    name="status"
                                    className="form-select"
                                    value={vaccineForm.status}
                                    onChange={handleVaccineChange}
                                >

                                    <option value="ACTIVE">
                                        ACTIVE
                                    </option>

                                    <option value="INACTIVE">
                                        INACTIVE
                                    </option>

                                </select>

                            </div>


                            <div className="col-12">

                                <button
                                    className="btn btn-success"
                                    onClick={saveVaccine}
                                >
                                    Add Vaccine
                                </button>

                            </div>

                        </div>

                    </div>

                </div>


                <div className="card shadow-sm">

                    <div className="card-header">

                        <strong>
                            Vaccine List
                        </strong>

                    </div>


                    <div
                        className="card-body"
                        style={{height: 450}}
                    >

                        <DataGrid
                            rows={vaccines}
                            columns={vaccineColumns}
                            loading={loading}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10, page: 0
                                    }
                                }
                            }}
                            slots={{
                                toolbar: GridToolbar
                            }}
                        />

                    </div>

                </div>

            </>)}


            {/* ====================================================
                        BATCH & STOCK
                    ==================================================== */}

            {vaccineSection === "batch" && (<>

                <div className="card shadow-sm mb-3">

                    <div className="card-header">

                        <strong>
                            Add Vaccine Batch
                        </strong>

                        <div className="small text-muted">
                            Vaccine inventory, batch,
                            expiry and cold-chain
                            information
                        </div>

                    </div>


                    <div className="card-body">

                        <div className="row g-3">

                            <div className="col-md-4">

                                <label className="form-label">
                                    Vaccine
                                </label>

                                <select
                                    className="form-select"
                                    name="vaccine"
                                    value={batchForm.vaccine}
                                    onChange={handleBatchChange}
                                >

                                    <option value="">
                                        Select Vaccine
                                    </option>

                                    {vaccines.map(vaccine => (<option
                                        key={vaccine.id}
                                        value={vaccine.id}
                                    >
                                        {vaccine.vaccine_name}
                                    </option>))}

                                </select>

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Batch / Lot Number
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="batch_number"
                                    value={batchForm.batch_number}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Manufacturer
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="manufacturer"
                                    value={batchForm.manufacturer}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Manufacturing Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    name="manufacturing_date"
                                    value={batchForm.manufacturing_date}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Expiry Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    name="expiry_date"
                                    value={batchForm.expiry_date}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Total Quantity
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    name="quantity"
                                    value={batchForm.quantity}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Available Quantity
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    name="available_quantity"
                                    value={batchForm.available_quantity}
                                    onChange={handleBatchChange}
                                    placeholder="Same as total if blank"
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Storage Location
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="storage_location"
                                    value={batchForm.storage_location}
                                    onChange={handleBatchChange}
                                    placeholder="Cold Chain / Refrigerator"
                                />

                            </div>


                            <div className="col-md-4">

                                <label className="form-label">
                                    Remarks
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="remarks"
                                    value={batchForm.remarks}
                                    onChange={handleBatchChange}
                                />

                            </div>


                            <div className="col-12">

                                <button
                                    className="btn btn-success"
                                    onClick={saveBatch}
                                    disabled={savingBatch}
                                >

                                    {savingBatch ? "Saving..." : "Add Batch"}

                                </button>

                            </div>

                        </div>

                    </div>

                </div>


                {/* ====================================================
                                STOCK SUMMARY
                            ==================================================== */}

                <div className="row g-3 mb-3">

                    <div className="col-md-3">

                        <div className="card shadow-sm">

                            <div className="card-body">

                                <small className="text-muted">
                                    Total Batches
                                </small>

                                <h4 className="mb-0">
                                    {batches.length}
                                </h4>

                            </div>

                        </div>

                    </div>


                    <div className="col-md-3">

                        <div className="card shadow-sm">

                            <div className="card-body">

                                <small className="text-muted">
                                    Available Batches
                                </small>

                                <h4 className="mb-0 text-success">

                                    {batches.filter(batch => getStockStatus(batch).label === "Available").length}

                                </h4>

                            </div>

                        </div>

                    </div>


                    <div className="col-md-3">

                        <div className="card shadow-sm">

                            <div className="card-body">

                                <small className="text-muted">
                                    Low Stock
                                </small>

                                <h4 className="mb-0 text-warning">

                                    {batches.filter(batch => getStockStatus(batch).label === "Low Stock").length}

                                </h4>

                            </div>

                        </div>

                    </div>


                    <div className="col-md-3">

                        <div className="card shadow-sm">

                            <div className="card-body">

                                <small className="text-muted">
                                    Expired
                                </small>

                                <h4 className="mb-0 text-danger">

                                    {batches.filter(batch => getStockStatus(batch).label === "Expired").length}

                                </h4>

                            </div>

                        </div>

                    </div>

                </div>


                {/* ====================================================
                                VACCINE STOCK
                            ==================================================== */}

                <div className="card shadow-sm">

                    <div className="card-header">

                        <strong>
                            Vaccine Batch & Stock
                        </strong>

                    </div>


                    <div
                        className="card-body"
                        style={{
                            height: 500
                        }}
                    >

                        <DataGrid

                            rows={batches}

                            loading={batchLoading}

                            columns={[
                                {
                                    field: "id",
                                    headerName: "ID",
                                    width: 70
                                },
                                {
                                    field: "batch_number",
                                    headerName: "Batch / Lot",
                                    width: 160,
                                    renderCell: (params) => (
                                        <span>
                {params.row?.batch_number || "-"}
            </span>
                                    )
                                },
                                {
                                    field: "vaccine",
                                    headerName: "Vaccine",
                                    flex: 1,
                                    minWidth: 180,
                                    renderCell: (params) => (
                                        <span>
                {params.row?.vaccine?.vaccine_name || "-"}
            </span>
                                    )
                                },
                                {
                                    field: "manufacturer",
                                    headerName: "Manufacturer",
                                    width: 160
                                },
                                {
                                    field: "manufacturing_date",
                                    headerName: "Manufacturing",
                                    width: 130
                                },
                                {
                                    field: "expiry_date",
                                    headerName: "Expiry",
                                    width: 130
                                },
                                {
                                    field: "received_date",
                                    headerName: "Received",
                                    width: 130
                                },
                                {
                                    field: "quantity_received",
                                    headerName: "Received Qty",
                                    width: 120
                                },
                                {
                                    field: "quantity_available",
                                    headerName: "Available",
                                    width: 110
                                },
                                {
                                    field: "quantity_used",
                                    headerName: "Used",
                                    width: 90
                                },
                                {
                                    field: "reorder_level",
                                    headerName: "Reorder",
                                    width: 90
                                },
                                {
                                    field: "storage_location",
                                    headerName: "Storage",
                                    width: 160
                                },
                                {
                                    field: "status",
                                    headerName: "Stock Status",
                                    width: 140,
                                    renderCell: (params) => {
                                        const stockStatus =
                                            getStockStatus(params.row);
                                        return (
                                            <span
                                                className={`badge ${
                                                    stockStatus.color === "success"
                                                        ? "bg-success"
                                                        : stockStatus.color === "warning"
                                                            ? "bg-warning text-dark"
                                                            : "bg-danger"
                                                }`}
                                            >
                {stockStatus.label}
            </span>
                                        );
                                    }
                                }
                            ]}
                            pageSizeOptions={[10, 25, 50, 100]}

                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10, page: 0
                                    }
                                }
                            }}

                            slots={{
                                toolbar: GridToolbar
                            }}

                        />

                    </div>

                </div>

            </>)}

        </>)}


        {/* ====================================================
                SCHEDULE MASTER
            ==================================================== */}

        {tab === "schedule" && (<>

            <div className="card shadow-sm mb-3">

                <div className="card-header">

                    <strong>
                        Vaccination Schedule Master
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row g-3">

                        <div className="col-md-3">

                            <label className="form-label">
                                Schedule Code
                            </label>

                            <input
                                type="text"
                                name="schedule_code"
                                className="form-control"
                                value={scheduleForm.schedule_code}
                                onChange={handleScheduleChange}
                                placeholder="Schedule Code"
                            />

                        </div>


                        <div className="col-md-5">

                            <label className="form-label">
                                Schedule Name
                            </label>

                            <input
                                type="text"
                                name="schedule_name"
                                className="form-control"
                                value={scheduleForm.schedule_name}
                                onChange={handleScheduleChange}
                                placeholder="Schedule Name"
                            />

                        </div>


                        <div className="col-md-4">

                            <label className="form-label">
                                Gender
                            </label>

                            <select
                                name="gender"
                                className="form-select"
                                value={scheduleForm.gender}
                                onChange={handleScheduleChange}
                            >

                                <option value="">
                                    All
                                </option>

                                <option value="MALE">
                                    Male
                                </option>

                                <option value="FEMALE">
                                    Female
                                </option>

                            </select>

                        </div>


                        <div className="col-md-8">

                            <label className="form-label">
                                Description
                            </label>

                            <input
                                type="text"
                                name="description"
                                className="form-control"
                                value={scheduleForm.description}
                                onChange={handleScheduleChange}
                            />

                        </div>


                        <div className="col-md-4">

                            <label className="form-label">
                                Status
                            </label>

                            <select
                                name="status"
                                className="form-select"
                                value={scheduleForm.status}
                                onChange={handleScheduleChange}
                            >

                                <option value="ACTIVE">
                                    ACTIVE
                                </option>

                                <option value="INACTIVE">
                                    INACTIVE
                                </option>

                            </select>

                        </div>


                        <div className="col-12">

                            <button
                                className="btn btn-success"
                                onClick={saveSchedule}
                            >
                                Add Schedule
                            </button>

                        </div>

                    </div>

                </div>

            </div>


            <div className="card shadow-sm">

                <div className="card-header">

                    <strong>
                        Vaccination Schedule List
                    </strong>

                </div>


                <div
                    className="card-body"
                    style={{height: 420}}
                >

                    <DataGrid
                        rows={schedules}
                        columns={scheduleColumns}
                        loading={loading}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10, page: 0
                                }
                            }
                        }}
                        slots={{
                            toolbar: GridToolbar
                        }}
                    />

                </div>

            </div>

        </>)}


        {/* ====================================================
                SCHEDULE DOSE
            ==================================================== */}

        {tab === "dose" && (<>

            <div className="card shadow-sm mb-3">

                <div className="card-header">

                    <strong>
                        Vaccination Schedule Dose
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row g-3">

                        <div className="col-md-6">

                            <label className="form-label">
                                Schedule
                            </label>

                            <select
                                name="schedule"
                                className="form-select"
                                value={doseForm.schedule}
                                onChange={handleDoseChange}
                            >

                                <option value="">
                                    Select Schedule
                                </option>

                                {schedules.map(schedule => (<option
                                    key={schedule.id}
                                    value={schedule.id}
                                >
                                    {schedule.schedule_name}
                                </option>))}

                            </select>

                        </div>


                        <div className="col-md-6">

                            <label className="form-label">
                                Vaccine
                            </label>

                            <select
                                name="vaccine"
                                className="form-select"
                                value={doseForm.vaccine}
                                onChange={handleDoseChange}
                            >

                                <option value="">
                                    Select Vaccine
                                </option>

                                {vaccines.map(vaccine => (<option
                                    key={vaccine.id}
                                    value={vaccine.id}
                                >
                                    {vaccine.vaccine_name}
                                </option>))}

                            </select>

                        </div>


                        <div className="col-md-2">

                            <label className="form-label">
                                Dose No.
                            </label>

                            <input
                                type="number"
                                min="1"
                                name="dose_number"
                                className="form-control"
                                value={doseForm.dose_number}
                                onChange={handleDoseChange}
                            />

                        </div>


                        <div className="col-md-4">

                            <label className="form-label">
                                Dose Name
                            </label>

                            <input
                                type="text"
                                name="dose_name"
                                className="form-control"
                                value={doseForm.dose_name}
                                onChange={handleDoseChange}
                                placeholder="Birth / Dose 1 / Booster"
                            />

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Age Value
                            </label>

                            <input
                                type="number"
                                name="age_value"
                                className="form-control"
                                value={doseForm.age_value}
                                onChange={handleDoseChange}
                            />

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Age Unit
                            </label>

                            <select
                                name="age_unit"
                                className="form-select"
                                value={doseForm.age_unit}
                                onChange={handleDoseChange}
                            >

                                <option value="">
                                    Select
                                </option>

                                <option value="DAYS">
                                    Days
                                </option>

                                <option value="WEEKS">
                                    Weeks
                                </option>

                                <option value="MONTHS">
                                    Months
                                </option>

                                <option value="YEARS">
                                    Years
                                </option>

                            </select>

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Interval Value
                            </label>

                            <input
                                type="number"
                                name="interval_value"
                                className="form-control"
                                value={doseForm.interval_value}
                                onChange={handleDoseChange}
                            />

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Interval Unit
                            </label>

                            <select
                                name="interval_unit"
                                className="form-select"
                                value={doseForm.interval_unit}
                                onChange={handleDoseChange}
                            >

                                <option value="">
                                    Select
                                </option>

                                <option value="DAYS">
                                    Days
                                </option>

                                <option value="WEEKS">
                                    Weeks
                                </option>

                                <option value="MONTHS">
                                    Months
                                </option>

                                <option value="YEARS">
                                    Years
                                </option>

                            </select>

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Minimum Interval
                            </label>

                            <input
                                type="number"
                                name="minimum_interval_value"
                                className="form-control"
                                value={doseForm.minimum_interval_value}
                                onChange={handleDoseChange}
                            />

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Minimum Interval Unit
                            </label>

                            <select
                                name="minimum_interval_unit"
                                className="form-select"
                                value={doseForm.minimum_interval_unit}
                                onChange={handleDoseChange}
                            >

                                <option value="">
                                    Select
                                </option>

                                <option value="DAYS">
                                    Days
                                </option>

                                <option value="WEEKS">
                                    Weeks
                                </option>

                                <option value="MONTHS">
                                    Months
                                </option>

                                <option value="YEARS">
                                    Years
                                </option>

                            </select>

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">
                                Status
                            </label>

                            <select
                                name="status"
                                className="form-select"
                                value={doseForm.status}
                                onChange={handleDoseChange}
                            >

                                <option value="ACTIVE">
                                    ACTIVE
                                </option>

                                <option value="INACTIVE">
                                    INACTIVE
                                </option>

                            </select>

                        </div>


                        <div className="col-md-3">

                            <div className="form-check mt-4">

                                <input
                                    type="checkbox"
                                    name="booster"
                                    className="form-check-input"
                                    checked={doseForm.booster}
                                    onChange={handleDoseChange}
                                />

                                <label className="form-check-label">
                                    Booster Dose
                                </label>

                            </div>

                        </div>


                        <div className="col-md-9">

                            <label className="form-label">
                                Remarks
                            </label>

                            <input
                                type="text"
                                name="remarks"
                                className="form-control"
                                value={doseForm.remarks}
                                onChange={handleDoseChange}
                            />

                        </div>


                        <div className="col-md-3 d-flex align-items-end">

                            <button
                                className="btn btn-success"
                                onClick={saveDose}
                            >
                                Add Dose
                            </button>

                        </div>

                    </div>

                </div>

            </div>


            <div className="card shadow-sm">

                <div className="card-header">

                    <strong>
                        Schedule Dose List
                    </strong>

                </div>


                <div
                    className="card-body"
                    style={{height: 450}}
                >

                    <DataGrid
                        rows={scheduleDoses}
                        columns={doseColumns}
                        loading={loading}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10, page: 0
                                }
                            }
                        }}
                        slots={{
                            toolbar: GridToolbar
                        }}
                    />

                </div>

            </div>

        </>)}

    </div>);
}
export default VaccinationManagement;